import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { courierApi } from '../api/courier-api';
import { courierKeys, courierQueries } from '../api/courier-queries';
import type {
  CourierParams,
  UpdateCourierStatusPayload,
  UpdateCourierPayload,
  CourierListResponse,
} from './schema';

/** Courier list with filter params */
export function useCouriers(params?: CourierParams) {
  return useQuery(courierQueries.list(params));
}

/** Courier counts across all 9 fleet tabs */
export function useCourierCounts() {
  return useQuery(courierQueries.counts());
}

/** Mutation to update courier status (block, unblock, activate) with cache patching */
export function useUpdateCourierStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCourierStatusPayload }) =>
      courierApi.updateStatus(id, payload),
    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries({ queryKey: courierKeys.all });

      // Optimistically patch couriers in cache
      queryClient.setQueriesData<CourierListResponse>(
        { queryKey: courierKeys.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.id === id
                ? {
                    ...item,
                    status: payload.status,
                    is_blocked: payload.is_blocked ?? item.is_blocked,
                    status_label:
                      payload.is_blocked || payload.status === 'blocked'
                        ? 'Blocked'
                        : item.status_label,
                  }
                : item,
            ),
          };
        },
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: courierKeys.all });
    },
  });
}

/** Mutation to edit courier profile / branch */
export function useUpdateCourier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCourierPayload }) =>
      courierApi.update(id, payload),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: courierKeys.all });
    },
  });
}
